import {
  generateCorrelationId,
  getCurrentCorrelationId,
  setCurrentCorrelationId,
} from "./correlation";
import { logEvent } from "./provider";
import type { TelemetryProperties } from "./types";

export type CapabilityOutcome =
  | "success"
  | "cancelled"
  | "declined_external"
  | "no_match"
  | "not_connected"
  | "unsupported_api"
  | "error";

export type CapabilitySurface = "read" | "compose" | "none";

export type CapabilityTrigger =
  | "natural_language"
  | "action_button"
  | "row_action"
  | "palette";

export type CapabilityWritePath =
  | "compose_fast"
  | "graph_reply"
  | "graph_replyall"
  | "graph_new"
  | "native_form"
  | "graph_event";

export interface CapabilityFacts {
  capabilityId?: string;
  surface?: CapabilitySurface;
  writePath?: CapabilityWritePath;
  committed?: boolean;
  wroteToDocument?: boolean;
  aiUsed?: boolean;
  graphUsed?: boolean;
  barVariant?: "review" | "outline" | "default" | "none";
  apiSetRequired?: string;
  confirmShown?: boolean;
  confirmAccepted?: boolean;
  recipientCount?: number;
  externalCount?: number;
  hitCount?: number;
  hitTotal?: number;
  threadMessages?: number;
  threadMessagesExact?: boolean;
  truncated?: boolean;
  matchCount?: number;
  appliedCount?: number;
  sectionCount?: number;
  termCount?: number;
  shapeCount?: number;
}

export interface CapabilityResult extends CapabilityFacts {
  outcome: CapabilityOutcome;
  errorCode?: string;
}

export interface CapabilityInit {
  capabilityId?: string;
  surface?: CapabilitySurface;
  trigger?: CapabilityTrigger;
  facts?: CapabilityFacts;
}

export interface CapabilitySpan {
  readonly correlationId: string;
  readonly isComplete: boolean;
  readonly facts: Readonly<CapabilityFacts>;
  observe(facts: CapabilityFacts): void;
  complete(result: CapabilityResult): void;
}

function readHostVersion(): string | undefined {
  try {
    const version = (
      globalThis as {
        Office?: { context?: { diagnostics?: { hostVersion?: string } } };
      }
    ).Office?.context?.diagnostics?.hostVersion;
    return typeof version === "string" && version.trim() ? version.trim() : undefined;
  } catch {
    return undefined;
  }
}

function debugLog(half: "invoke" | "outcome", correlationId: string, properties: TelemetryProperties): void {
  if (!import.meta.env.DEV) return;
  console.info(
    `[capability] ${half} ${correlationId.slice(0, 8)}`,
    properties,
  );
}

function withSpanCorrelation(id: string, emit: () => void): void {
  const previous = getCurrentCorrelationId();
  setCurrentCorrelationId(id);
  try {
    emit();
  } finally {
    setCurrentCorrelationId(previous);
  }
}

function definedEntries(facts: CapabilityFacts): TelemetryProperties {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(facts)) {
    if (value !== undefined && value !== null) out[key] = value;
  }
  return out as TelemetryProperties;
}

function mergeFacts(base: CapabilityFacts, incoming: CapabilityFacts): CapabilityFacts {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(incoming)) {
    if (value !== undefined) merged[key] = value;
  }
  return merged as CapabilityFacts;
}

export function startCapability(init: CapabilityInit = {}): CapabilitySpan {
  const correlationId = getCurrentCorrelationId() ?? generateCorrelationId();
  const startedAt = Date.now();
  const hostVersion = readHostVersion();

  let facts: CapabilityFacts = mergeFacts(
    {
      ...(init.capabilityId !== undefined ? { capabilityId: init.capabilityId } : {}),
      ...(init.surface !== undefined ? { surface: init.surface } : {}),
    },
    init.facts ?? {},
  );
  let completed = false;

  withSpanCorrelation(correlationId, () => {
    const properties: TelemetryProperties = {
      ...definedEntries(facts),
      ...(init.trigger ? { trigger: init.trigger } : {}),
      ...(hostVersion ? { hostVersion } : {}),
    };
    logEvent("capability.invoke", "usage", properties);
    debugLog("invoke", correlationId, properties);
  });

  return {
    correlationId,
    get isComplete() {
      return completed;
    },
    get facts() {
      return facts;
    },
    observe(incoming: CapabilityFacts): void {
      if (completed) return;
      facts = mergeFacts(facts, incoming);
    },
    complete(result: CapabilityResult): void {
      if (completed) return;
      completed = true;
      const { outcome, errorCode, ...resultFacts } = result;
      const merged = mergeFacts(facts, resultFacts);
      withSpanCorrelation(correlationId, () => {
        const properties: TelemetryProperties = {
          ...definedEntries(merged),
          outcome,
          ...(errorCode ? { errorCode } : {}),
          ...(init.trigger ? { trigger: init.trigger } : {}),
          ...(hostVersion ? { hostVersion } : {}),
          durationMs: Date.now() - startedAt,
        };
        logEvent("capability.outcome", "usage", properties);
        debugLog("outcome", correlationId, properties);
      });
    },
  };
}

export function noopCapabilitySpan(): CapabilitySpan {
  let completed = false;
  return {
    correlationId: "",
    get isComplete() {
      return completed;
    },
    facts: {},
    observe() {
      return;
    },
    complete() {
      completed = true;
    },
  };
}

export type { AddInHost } from "./types";
